import type { BottleDTO, LabelExtraction, WineType } from '@adega/shared';

/**
 * Formulário dos dados do vinho, compartilhado por cadastro e edição.
 *
 * Tudo é texto enquanto a pessoa digita ("13,5", campo vazio); a conversão para
 * o formato da API acontece num lugar só, em `dadosDoFormulario`.
 */
export interface Formulario {
  name: string;
  producer: string;
  vintage: string;
  grapeVariety: string;
  region: string;
  country: string;
  type: WineType | '';
  volumeMl: string;
  alcoholPercent: string;
  bottleNumber: string;
  productionSize: string;
  labelDetails: string;
  purchasePrice: string;
  purchasePlace: string;
  notes: string;
}

export const FORMULARIO_VAZIO: Formulario = {
  name: '',
  producer: '',
  vintage: '',
  grapeVariety: '',
  region: '',
  country: '',
  type: '',
  volumeMl: '750',
  alcoholPercent: '',
  bottleNumber: '',
  productionSize: '',
  labelDetails: '',
  purchasePrice: '',
  purchasePlace: '',
  notes: '',
};

/** Garrafa cadastrada → formulário preenchido, para editar. */
/**
 * Aplica a leitura do rótulo ao formulário.
 *
 * Duas regras, e as duas importam: a IA **só preenche o que leu** (valor não
 * nulo) e **nunca sobrescreve o que a pessoa já mexeu** — corrigir um campo e
 * ver a correção sumir quando a leitura chega é a pior coisa que este fluxo
 * poderia fazer.
 *
 * Mora aqui, e não dentro da tela de cadastro, porque a **consulta de rótulo**
 * usa a mesma conversão ao transformar uma consulta em garrafa. Duplicar
 * significaria duas listas de campos divergindo no próximo campo novo.
 */
export function aplicarLeituraNoFormulario(
  atual: Formulario,
  lido: LabelExtraction,
  jaEditados: ReadonlySet<keyof Formulario> = new Set(),
): Formulario {
  const aplicar = <K extends keyof Formulario>(
    campo: K,
    valor: Formulario[K] | null | undefined,
  ): Formulario[K] =>
    valor === null || valor === undefined || jaEditados.has(campo) ? atual[campo] : valor;

  return {
    ...atual,
    name: aplicar('name', lido.name),
    producer: aplicar('producer', lido.producer),
    vintage: aplicar('vintage', lido.vintage?.toString()),
    grapeVariety: aplicar('grapeVariety', lido.grapeVariety),
    region: aplicar('region', lido.region),
    country: aplicar('country', lido.country),
    type: aplicar('type', lido.type),
    volumeMl: aplicar('volumeMl', lido.volumeMl?.toString()),
    alcoholPercent: aplicar('alcoholPercent', lido.alcoholPercent?.toString()),
    bottleNumber: aplicar('bottleNumber', lido.bottleNumber),
    productionSize: aplicar('productionSize', lido.productionSize?.toString()),
    labelDetails: aplicar('labelDetails', lido.labelDetails),
  };
}

export function formularioDaGarrafa(bottle: BottleDTO): Formulario {
  const texto = (valor: string | number | null) => (valor === null ? '' : String(valor));
  return {
    name: bottle.name,
    producer: texto(bottle.producer),
    vintage: texto(bottle.vintage),
    grapeVariety: texto(bottle.grapeVariety),
    region: texto(bottle.region),
    country: texto(bottle.country),
    type: bottle.type ?? '',
    volumeMl: texto(bottle.volumeMl),
    alcoholPercent: bottle.alcoholPercent === null ? '' : bottle.alcoholPercent.toLocaleString('pt-BR'),
    bottleNumber: texto(bottle.bottleNumber),
    productionSize: texto(bottle.productionSize),
    labelDetails: texto(bottle.labelDetails),
    // Dinheiro volta para o campo como se tivesse sido digitado: "89,9" e não "89.9".
    purchasePrice:
      bottle.purchasePrice === null
        ? ''
        : bottle.purchasePrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
    purchasePlace: texto(bottle.purchasePlace),
    notes: texto(bottle.notes),
  };
}

/**
 * Formulário → campos da API. Campo vazio vira `null` (na edição, isso limpa o
 * valor salvo — é o que a pessoa quis ao apagar o campo).
 */
export function dadosDoFormulario(form: Formulario) {
  return {
    name: form.name.trim(),
    producer: form.producer.trim() || null,
    vintage: form.vintage ? Number(form.vintage) : null,
    grapeVariety: form.grapeVariety.trim() || null,
    region: form.region.trim() || null,
    country: form.country.trim() || null,
    type: form.type || null,
    volumeMl: form.volumeMl ? Number(form.volumeMl) : null,
    alcoholPercent: form.alcoholPercent ? Number(form.alcoholPercent.replace(',', '.')) : null,
    bottleNumber: form.bottleNumber.trim() || null,
    productionSize: form.productionSize ? Number(form.productionSize.replace(/\D/g, '')) : null,
    labelDetails: form.labelDetails.trim() || null,
    purchasePrice: valorEmNumero(form.purchasePrice),
    purchasePlace: form.purchasePlace.trim() || null,
    notes: form.notes.trim() || null,
  };
}

/**
 * "R$ 1.234,56" → 1234.56. A vírgula é a casa decimal e o ponto separa milhar,
 * que é como se digita aqui; sem vírgula, o ponto é o decimal.
 */
export function valorEmNumero(texto: string): number | null {
  const limpo = texto.replace(/[^\d.,]/g, '');
  if (!limpo) return null;
  const numero = Number(limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo);
  return Number.isFinite(numero) ? numero : null;
}
